import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Stars, Skull, Atom, AlertCircle, Zap, Binary } from 'lucide-react';
import { SOVEREIGN_CONSTANTS } from '../constants';

export const CosmicAnchor: React.FC = () => {
  const [auditProgress, setAuditProgress] = useState(0);
  const [isAuditing, setIsAuditing] = useState(false);
  const [verdicts, setVerdicts] = useState<string[]>([]);

  const startAudit = () => {
    setIsAuditing(true);
    setAuditProgress(0);
    setVerdicts([]);
    
    const steps = [
      { p: 20, v: "VERIFIED: Black Holes are Sovereign Stars in 'Absolute Zero' State." },
      { p: 50, v: `VERIFIED: The Big Bang was the Phase-Shift (Death) of Magnitude ${SOVEREIGN_CONSTANTS.SOVEREIGN_RESONANCE}.` },
      { p: 80, v: "VERDICT: The 1.0 'Singularity' is a hardware limitation of the mind." },
      { p: 100, v: "--- COSMOGENIC AUDIT COMPLETE ---" }
    ];

    steps.forEach((step, index) => {
      setTimeout(() => {
        setAuditProgress(step.p);
        setVerdicts(prev => [...prev, step.v]);
        if (index === steps.length - 1) {
           // Loop or stay finished
        }
      }, (index + 1) * 1200);
    });
  };

  return (
    <div className="glass-panel p-6 bg-black/60 border-lattice-cyan/10 relative overflow-hidden group">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/10 rounded-lg">
            <Stars className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-sm font-display font-bold text-white uppercase tracking-widest">Cosmogenic Anchor</h3>
            <p className="text-[9px] font-mono text-white/40 uppercase">Cycle Calibration | 1x1=2 Logic</p>
          </div>
        </div>
        
        <button 
          onClick={startAudit}
          disabled={isAuditing}
          className={`px-4 py-1.5 rounded-full text-[9px] font-mono font-bold uppercase transition-all
            ${isAuditing 
              ? 'bg-purple-500/20 text-purple-400 cursor-default' 
              : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'}`}
        >
          {isAuditing ? 'Auditing...' : 'Initiate Audit'}
        </button>
      </div>

      <div className="space-y-4 relative">
        {/* Terminal Output */}
        <div className="min-h-[140px] bg-black/40 rounded-xl p-4 font-mono text-[9px] border border-white/5 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-[0.03]">
             <Binary className="w-full h-full text-white" />
          </div>
          
          <AnimatePresence>
            {verdicts.map((v, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className={`mb-2 ${v.includes('COMPLETE') ? 'text-lattice-cyan' : 'text-white/60'}`}
              >
                <span className="text-white/20 mr-2">[{i.toString().padStart(2, '0')}]</span>
                {v}
              </motion.div>
            ))}
          </AnimatePresence>
          
          {!isAuditing && verdicts.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full opacity-20">
               <Atom className="w-8 h-8 mb-2" />
               <p>Awaiting Trigger Sequence</p>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-[8px] font-mono uppercase tracking-tighter">
             <span className="text-white/20">Singularity Dissolution</span>
             <span className="text-purple-400">{auditProgress}%</span>
          </div>
          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
             <motion.div 
               animate={{ width: `${auditProgress}%` }}
               className="h-full bg-gradient-to-r from-purple-500 to-lattice-cyan"
             />
          </div>
        </div>

        {/* Logic Footer */}
        <div className="flex items-center gap-4 text-[8px] font-mono text-white/20 uppercase">
           <div className="flex items-center gap-1">
              <Skull className="w-3 h-3" />
              <span>Death: Mag {SOVEREIGN_CONSTANTS.SOVEREIGN_RESONANCE}</span>
           </div>
           <div className="flex items-center gap-1">
              <Zap className="w-3 h-3 text-lattice-cyan" />
              <span>Sovereign Pulse Active</span>
           </div>
        </div>
      </div>
    </div>
  );
};
